/**
 * CheckoutUrlBuilder - Classe utilitária centralizada para geração de URLs de checkout
 *
 * REGRA CRÍTICA:
 * - SEMPRE usar product_code ou uuid, NUNCA ID numérico
 * - SEMPRE usar checkout_code, NUNCA ID numérico
 * - SEMPRE usar affiliate_code (formato aff_xxxxx), NUNCA link_code ou ID
 *
 * FORMATO CORRETO:
 * https://app.pagmus.com.br/#/checkout/{productCode}/{checkoutCode}?aff={affiliateCode}
 *
 * @version 1.0.0
 * @author Pagmus Team
 * @date 25/01/2026
 */

import { FRONTEND_URL } from '../config/api';

class CheckoutUrlBuilder {
  constructor() {
    // Base URL do frontend (de variável de ambiente ou fallback)
    this.baseUrl = FRONTEND_URL || window.location.origin;
  }

  /**
   * Resolve o identificador do produto (preferência: product_code > uuid > id)
   * @param {Object|string} product - Objeto do produto ou identificador direto
   * @returns {string|null} - Identificador resolvido
   */
  resolveProductIdentifier(product) {
    if (!product) return null;

    // Se já é uma string, assume que é um identificador válido
    if (typeof product === 'string') return product;

    // Preferência: product_code > uuid > id (fallback)
    return product.product_code || product.uuid || product.id?.toString();
  }

  /**
   * Resolve o identificador do checkout (preferência: checkout_code > id)
   * @param {Object|string} checkout - Objeto do checkout ou identificador direto
   * @returns {string|null} - Identificador resolvido
   */
  resolveCheckoutIdentifier(checkout) {
    if (!checkout) return null;

    // Se já é uma string, assume que é um identificador válido
    if (typeof checkout === 'string') return checkout;

    // Preferência: checkout_code > id (fallback)
    return checkout.checkout_code || checkout.id?.toString();
  }

  /**
   * Resolve o código do afiliado (preferência: affiliate_code)
   * @param {Object|string} affiliate - Objeto do afiliado ou código direto
   * @returns {string|null} - Código do afiliado
   */
  resolveAffiliateCode(affiliate) {
    if (!affiliate) return null;

    // Se já é uma string, assume que é um código válido
    if (typeof affiliate === 'string') return affiliate;

    // Preferência: affiliate_code
    return affiliate.affiliate_code;
  }

  /**
   * Gera URL de checkout completa
   *
   * @param {Object|string} product - Objeto do produto (precisa ter product_code ou uuid)
   * @param {Object|string} checkout - Objeto do checkout (precisa ter checkout_code)
   * @param {Object|string|null} affiliate - Objeto do afiliado ou código (formato: aff_xxxxx)
   * @returns {string|null} URL completa do checkout
   *
   * @example
   * // Com objetos completos
   * checkoutUrlBuilder.buildUrl(product, checkout, affiliate);
   *
   * // Com identificadores diretos
   * checkoutUrlBuilder.buildUrl('Prod00000003', 'chk_dd7698e5c520', 'aff_3a803444849c');
   */
  buildUrl(product, checkout, affiliate = null) {
    const productIdentifier = this.resolveProductIdentifier(product);
    const checkoutIdentifier = this.resolveCheckoutIdentifier(checkout);

    if (!productIdentifier) {
      console.error('[CheckoutUrlBuilder] Produto sem identificador válido:', product);
      return null;
    }

    if (!checkoutIdentifier) {
      console.error('[CheckoutUrlBuilder] Checkout sem identificador válido:', checkout);
      return null;
    }

    // Construir URL base
    let url = `${this.baseUrl}/#/checkout/${productIdentifier}/${checkoutIdentifier}`;

    // Adicionar affiliate code se fornecido
    const affiliateCode = this.resolveAffiliateCode(affiliate);
    if (affiliateCode) {
      url += `?aff=${affiliateCode}`;
    }

    return url;
  }

  /**
   * Gera URL para afiliado (com affiliate_code obrigatório)
   * @param {Object|string} product - Produto
   * @param {Object|string} checkout - Checkout
   * @param {Object|string} affiliate - Afiliado (obrigatório)
   * @returns {string|null} URL com parâmetro de afiliado
   */
  buildAffiliateUrl(product, checkout, affiliate) {
    const affiliateCode = this.resolveAffiliateCode(affiliate);

    if (!affiliateCode) {
      console.error('[CheckoutUrlBuilder] affiliateCode é obrigatório para URL de afiliado');
      return null;
    }

    return this.buildUrl(product, checkout, affiliate);
  }

  /**
   * Gera URL orgânica (sem afiliado)
   * @param {Object|string} product - Produto
   * @param {Object|string} checkout - Checkout
   * @returns {string|null} URL sem parâmetro de afiliado
   */
  buildOrganicUrl(product, checkout) {
    return this.buildUrl(product, checkout, null);
  }

  /**
   * Valida se uma URL de checkout está no formato correto
   * @param {string} url - URL a ser validada
   * @returns {boolean} True se a URL está no formato correto
   */
  validateUrl(url) {
    if (!url || typeof url !== 'string') return false;

    // Padrão: protocolo://domínio/#/checkout/identificador/identificador(?aff=código)?
    const pattern = /^https?:\/\/[^/]+\/#\/checkout\/[^/]+\/[^/?]+((\?|&)aff=[^&]+)?$/;
    return pattern.test(url);
  }

  /**
   * Extrai os componentes de uma URL de checkout
   * @param {string} url - URL de checkout
   * @returns {Object|null} Objeto com productCode, checkoutCode e affiliateCode
   */
  parseUrl(url) {
    if (!url || typeof url !== 'string') return null;

    try {
      const urlObj = new URL(url);
      const hash = urlObj.hash; // /#/checkout/Prod00000003/chk_xxxxx

      const match = hash.match(/^#\/checkout\/([^/]+)\/([^/?]+)/);
      if (!match) return null;

      const affiliateCode = urlObj.searchParams.get('aff') ||
                            new URLSearchParams(hash.split('?')[1] || '').get('aff');

      return {
        productCode: match[1],
        checkoutCode: match[2],
        affiliateCode: affiliateCode || null
      };
    } catch (e) {
      console.error('[CheckoutUrlBuilder] Erro ao parsear URL:', e);
      return null;
    }
  }

  /**
   * Verifica se o identificador é um ID numérico (que não deve ser usado)
   * @param {string} identifier - Identificador a verificar
   * @returns {boolean} True se é um ID numérico
   */
  isNumericId(identifier) {
    return /^\d+$/.test(identifier);
  }

  /**
   * Valida se os identificadores estão no formato correto
   * @param {string} productIdentifier - Identificador do produto
   * @param {string} checkoutIdentifier - Identificador do checkout
   * @returns {Object} Objeto com isValid e warnings
   */
  validateIdentifiers(productIdentifier, checkoutIdentifier) {
    const warnings = [];

    if (this.isNumericId(productIdentifier)) {
      warnings.push(`Produto usando ID numérico: ${productIdentifier}. Use product_code ou uuid.`);
    }

    if (this.isNumericId(checkoutIdentifier)) {
      warnings.push(`Checkout usando ID numérico: ${checkoutIdentifier}. Use checkout_code.`);
    }

    if (warnings.length > 0) {
      console.warn('[CheckoutUrlBuilder] Warnings:', warnings);
    }

    return {
      isValid: warnings.length === 0,
      warnings
    };
  }
}

// Exportar instância singleton para uso imediato
export const checkoutUrlBuilder = new CheckoutUrlBuilder();

// Exportar classe para casos onde nova instância é necessária
export default CheckoutUrlBuilder;
